/**
 * BrowserFsDirectory — the File System Access API behind the filesystem port.
 * This is the only file in the codebase that knows FileSystemDirectoryHandle exists.
 */

import { FileMissingError, splitPath, type FsDirectory, type FsEntry, type FsFile } from './fs-port';
import { forDom } from './bytes';

/**
 * One file behind a FileSystemFileHandle. Exported because loose files picked
 * individually are wrapped the same way, just without a parent directory.
 */
export class BrowserFsFile implements FsFile {
  constructor(private readonly handle: FileSystemFileHandle) {}

  private cached: File | null = null;

  private async raw(): Promise<File> {
    this.cached ??= await this.handle.getFile();
    return this.cached;
  }

  get name(): string {
    return this.handle.name;
  }

  // Populated by BrowserFsDirectory.file() before the object escapes.
  size = 0;
  lastModified = 0;

  async prime(): Promise<this> {
    const file = await this.raw();
    this.size = file.size;
    this.lastModified = file.lastModified;
    return this;
  }

  async bytes(): Promise<Uint8Array> {
    return new Uint8Array(await (await this.raw()).arrayBuffer());
  }

  async blob(): Promise<Blob> {
    return this.raw();
  }
}

export class BrowserFsDirectory implements FsDirectory {
  constructor(private readonly handle: FileSystemDirectoryHandle) {}

  get name(): string {
    return this.handle.name;
  }

  /** The underlying handle, needed only by HandleStore for persistence. */
  get raw(): FileSystemDirectoryHandle {
    return this.handle;
  }

  async list(): Promise<FsEntry[]> {
    const entries: FsEntry[] = [];
    // @ts-expect-error — values() is async-iterable but not yet in every lib.dom
    for await (const child of this.handle.values()) {
      entries.push({ kind: child.kind, name: child.name } as FsEntry);
    }
    return entries;
  }

  async directory(name: string, create = false): Promise<FsDirectory> {
    const child = await this.handle.getDirectoryHandle(name, { create });
    return new BrowserFsDirectory(child);
  }

  async file(relPath: string): Promise<FsFile> {
    const { dirs, filename } = splitPath(relPath);
    let dir = this.handle;
    try {
      for (const segment of dirs) dir = await dir.getDirectoryHandle(segment);
      const handle = await dir.getFileHandle(filename);
      return await new BrowserFsFile(handle).prime();
    } catch {
      throw new FileMissingError(relPath);
    }
  }

  async has(relPath: string): Promise<boolean> {
    try {
      await this.file(relPath);
      return true;
    } catch {
      return false;
    }
  }

  async write(name: string, bytes: Uint8Array): Promise<void> {
    const handle = await this.handle.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    try {
      await writable.write(forDom(bytes));
    } finally {
      await writable.close();
    }
  }
}
